import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useContent } from '../hooks/useContent';
import { helpTips } from '../config/helpMessages';
import DeleteAccountModal from './DeleteAccountModal';
import { hasAIConsent, revokeAIConsent, grantAIConsent } from './AIConsentModal';
import '../styles/components/profileTabs.css';

interface AccountTabProps {
  onShowQuickStartGuide?: () => void;
}

const AccountTab: React.FC<AccountTabProps> = ({ onShowQuickStartGuide }) => {
  const { user, logout, updateDisplayName, updateLocation, deleteAccount } = useAuth();
  const { profile } = useContent();

  const [nameInput, setNameInput] = useState(user ? user.display_name : '');
  const [locationInput, setLocationInput] = useState(user ? (user.location_text || '') : '');
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingLocation, setIsSavingLocation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [helpResetMessage, setHelpResetMessage] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [aiConsentEnabled, setAiConsentEnabled] = useState(hasAIConsent(user?.id));

  // Sync location input when user data loads/changes
  useEffect(() => {
    if (user?.location_text && !locationInput) {
      setLocationInput(user.location_text);
    }
  }, [user?.location_text]);

  // Re-sync with localStorage when tab becomes visible (consent may have changed elsewhere)
  useEffect(() => {
    setAiConsentEnabled(hasAIConsent(user?.id));
  });

  if (!user) return null;

  const isAdmin = user.role === 'admin';

  const handleResetHelpGuides = () => {
    // Reset Quick Start Guide
    localStorage.removeItem('hasSeenQuickStartGuide');
    
    // Reset all context-sensitive help tips
    Object.keys(helpTips).forEach(tipId => {
      localStorage.removeItem(`help-tip-dismissed-${tipId}`);
    });
    
    setHelpResetMessage('Help guides reset! Showing Quick Start Guide...');
    
    // Show the Quick Start Guide immediately
    setTimeout(() => {
      setHelpResetMessage(null);
      onShowQuickStartGuide?.();
    }, 1000);
  };

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === user.display_name) {
      return;
    }
    setIsSaving(true);
    setError(null);
    const result = await updateDisplayName(trimmed);
    if (!result.success) {
      setError(result.error || 'Failed to update name');
    }
    setIsSaving(false);
  };

  return (
    <div className="account-tab">
      <div className="account-card">
        <div className="profile-section account-section-left">
          <div className="profile-info-list">
            {/* Name field - full width */}
            <div className="profile-info-row">
              <div className="profile-info-item" style={{ width: '100%' }}>
                <span className="profile-info-label">Name</span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '4px' }}>
                  <input
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    className="account-name-input"
                  />
                  <button
                    type="button"
                    onClick={handleSaveName}
                    disabled={isSaving || !nameInput.trim() || nameInput.trim() === user.display_name}
                    className="account-save-btn"
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>

            {/* Email field */}
            <div className="profile-info-row">
              <div className="profile-info-item" style={{ width: '100%' }}>
                <span className="profile-info-label">Email</span>
                <span className="profile-info-value">{user.email}</span>
              </div>
            </div>

            {/* Location field */}
            <div className="profile-info-row">
              <div className="profile-info-item" style={{ width: '100%' }}>
                <span className="profile-info-label">Location</span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '4px' }}>
                  <input
                    type="text"
                    value={locationInput}
                    onChange={(e) => setLocationInput(e.target.value)}
                    placeholder="e.g. Portland, OR"
                    className="account-name-input"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      const trimmed = locationInput.trim();
                      if (trimmed === (user.location_text || '')) return;
                      setIsSavingLocation(true);
                      setError(null);
                      const result = await updateLocation(trimmed);
                      if (!result.success) {
                        setError(result.error || 'Failed to update location');
                      }
                      setIsSavingLocation(false);
                    }}
                    disabled={isSavingLocation || locationInput.trim() === (user.location_text || '')}
                    className="account-save-btn"
                  >
                    {isSavingLocation ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="profile-info-row">
                <div className="profile-info-item profile-info-left">
                  <span className="profile-info-label" style={{ color: '#D0021B' }}>{error}</span>
                </div>
              </div>
            )}

            {/* ID and Account Type on same line */}
            <div className="profile-info-row">
              <div className="profile-info-item profile-info-left">
                <span className="profile-info-label">{profile.user_id_label}</span>
                <span className="profile-info-value">{user.id}</span>
              </div>
              <div className="profile-info-item profile-info-right">
                <span className="profile-info-label">{profile.account_type_label}</span>
                <span className="profile-info-value">
                  {isAdmin ? profile.account_type_admin : profile.account_type_standard}
                </span>
              </div>
            </div>
          </div>

          <button onClick={logout} className="profile-logout-btn">
            {profile.logout || 'Logout'}
          </button>
        </div>
      </div>

      {/* Help & Support Section */}
      <div className="account-card account-card-padded">
        <h3 className="account-section-title">
          Help & Support
        </h3>
        <div className="account-support-list">
          <button onClick={handleResetHelpGuides} className="account-support-item">
            <span className="account-support-icon">💡</span>
            <div>
              <div className="account-support-item-title">Reset Help Guides</div>
              <div className="account-support-item-desc">
                Show the Quick Start tutorial and tips again
              </div>
            </div>
          </button>
          {helpResetMessage && (
            <div className="account-success-message">
              <span>✓</span>
              {helpResetMessage}
            </div>
          )}
          
          <div className="account-support-item">
            <span className="account-support-icon">🤖</span>
            <div style={{ flex: 1 }}>
              <div className="account-support-item-title">AI Photo Analysis</div>
              <div className="account-support-item-desc">
                Allow sending photos to Google Gemini / OpenAI for item identification
              </div>
            </div>
            <button
              onClick={() => {
                if (aiConsentEnabled) {
                  revokeAIConsent(user?.id);
                  setAiConsentEnabled(false);
                } else {
                  grantAIConsent(user?.id);
                  setAiConsentEnabled(true);
                }
              }}
              className={`account-toggle ${aiConsentEnabled ? 'active' : ''}`}
            >
              <div className="account-toggle-knob" />
            </button>
          </div>

          <button onClick={() => window.location.reload()} className="account-support-item">
            <span className="account-support-icon">🔄</span>
            <div>
              <div className="account-support-item-title">Refresh App</div>
              <div className="account-support-item-desc">
                Reload to get the latest version
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="account-card">
        <div className="account-danger-header">
          <h3 className="account-danger-title">
            ⚠️ Danger Zone
          </h3>
        </div>
        <div className="profile-section account-section-left">
          <button
            onClick={() => setShowDeleteModal(true)}
            className="account-delete-btn"
          >
            <span className="account-support-icon">🗑️</span>
            <div>
              <div className="account-delete-btn-title">Delete Account</div>
              <div className="account-delete-btn-desc">
                Permanently delete your account and all data
              </div>
            </div>
          </button>
        </div>
      </div>

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          await deleteAccount();
        }}
      />
    </div>
  );
};

export default AccountTab;
